// frontend-react/src/services/boxScoreApi.js
// Box Score API Service - Season Replay Phase 6A

import apiService from './apiService';

/**
 * Fetch box score data for a specific team and date
 * @param {string} leagueId - League UUID
 * @param {string} teamId - Team UUID
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Promise<Object>} Box score data with hitters, pitchers, team totals, and summary stats
 */
export async function fetchBoxScore(leagueId, teamId, date) {
  try {
    console.log(`📊 Fetching box score: league=${leagueId}, team=${teamId}, date=${date}`);
    
    const response = await apiService.get(
      `/api/leagues/${leagueId}/teams/${teamId}/box-score`,
      {
        params: { date }
      }
    );
    
    if (!response.data || !response.data.success) {
      throw new Error(response.data?.message || 'Failed to fetch box score');
    }
    
    console.log('✅ Box score data received:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Error fetching box score:', error);
    throw error;
  }
}

/**
 * Fetch available dates with box score data for a team in a season
 * @param {string} leagueId - League UUID
 * @param {string} teamId - Team UUID
 * @param {number} seasonYear - Season year (omit to let backend use league's current_season)
 * @returns {Promise<Object>} Available dates array and count
 */
export async function fetchAvailableDates(leagueId, teamId, seasonYear = null) {
  try {
    console.log(`📅 Fetching available dates: league=${leagueId}, team=${teamId}, year=${seasonYear || '(league default)'}`);

    const params = {};
    if (seasonYear != null) {
      params.season_year = seasonYear;
    }

    const response = await apiService.get(
      `/api/leagues/${leagueId}/teams/${teamId}/available-dates`,
      { params }
    );
    
    if (!response.data || !response.data.success) {
      throw new Error(response.data?.message || 'Failed to fetch available dates');
    }
    
    console.log('✅ Available dates received:', response.data.count, 'dates');
    return response.data;
    
  } catch (error) {
    console.error('❌ Error fetching available dates:', error);
    throw error;
  }
}

/**
 * Get the current Eastern Time date as YYYY-MM-DD, applying the 6 AM ET
 * day boundary: before 6 AM ET, "today" is still yesterday.
 */
function getEasternDateWith6AMBoundary() {
  const now = new Date();
  const etStr = now.toLocaleString('en-US', { timeZone: 'America/New_York', hour12: false });
  const etParts = etStr.split(', ');
  const timeParts = etParts[1]?.split(':') ?? [];
  const hour = parseInt(timeParts[0], 10);
  // Before 6 AM ET, "today" is still yesterday
  if (hour < 6) {
    const d = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    return d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  }
  return now.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

/**
 * Get yesterday's date in YYYY-MM-DD format (ET with 6 AM boundary)
 * @returns {string} Yesterday's date in Eastern Time
 */
export function getYesterdayDate() {
  const todayET = getEasternDateWith6AMBoundary();
  const d = new Date(todayET + 'T12:00:00');
  d.setDate(d.getDate() - 1);
  return d.toLocaleDateString('en-CA');
}

/**
 * Format date for display (e.g., "Oct 20, 2025")
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} Formatted date
 */
export function formatDateForDisplay(dateString) {
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Get next date from a date string
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} Next date in YYYY-MM-DD format
 */
export function getNextDate(dateString) {
  const date = new Date(dateString + 'T12:00:00');
  date.setDate(date.getDate() + 1);
  return date.toLocaleDateString('en-CA');
}

/**
 * Get previous date from a date string
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} Previous date in YYYY-MM-DD format
 */
export function getPreviousDate(dateString) {
  const date = new Date(dateString + 'T12:00:00');
  date.setDate(date.getDate() - 1);
  return date.toLocaleDateString('en-CA');
}

/**
 * Check if a date is today (ET with 6 AM boundary)
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {boolean} True if date is today in Eastern Time
 */
export function isToday(dateString) {
  return dateString === getEasternDateWith6AMBoundary();
}

/**
 * Get today's date in YYYY-MM-DD format (ET with 6 AM boundary)
 * @returns {string} Today's date in Eastern Time
 */
export function getTodayDate() {
  return getEasternDateWith6AMBoundary();
}

/**
 * Fetch league-wide box score rankings for a specific date
 * @param {string} leagueId - League UUID
 * @param {string} teamId - Your team UUID
 * @param {string} date - Date in YYYY-MM-DD format (optional, defaults to yesterday)
 * @returns {Promise<Object>} Rankings data with your team's rank for each stat category
 */
export async function fetchBoxScoreRankings(leagueId, teamId, date) {
  try {
    console.log(`🏆 Fetching box score rankings: league=${leagueId}, team=${teamId}, date=${date}`);
    
    const params = { team_id: teamId };
    if (date) {
      params.date = date;
    }
    
    const response = await apiService.get(
      `/api/leagues/${leagueId}/box-score-rankings`,
      { params }
    );
    
    if (!response.data || !response.data.success) {
      throw new Error(response.data?.message || 'Failed to fetch box score rankings');
    }
    
    console.log('✅ Box score rankings received:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Error fetching box score rankings:', error);
    throw error;
  }
}
